import React, { useEffect, useState, forwardRef } from "react";
import { useSelector } from "react-redux";
import threeMlogo from "./../../assets/images/solventum.svg";
import { getUser } from "../../Shared/AppContext/selectors";

const PrintQuote = forwardRef(({
  orderId,
  totalCost,
  shoppingCartItems
}, ref) => {
  const itemCurrency = shoppingCartItems?.[0]?.currency || "USD";
  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: itemCurrency,
  });

  const userDetails = useSelector(getUser);

  function formatDate(date) {
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
  const [quoteDate, setQuoteDate] = useState(formatDate(new Date()));

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteDate(formatDate(new Date()));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const splitProductDescription = (description, partNumber, isMainheader) => {
    if (!description) return null;
    const normalizedPartNumber = partNumber ? `${partNumber},` : "";
    const updatedString = normalizedPartNumber
      ? description.replace(normalizedPartNumber, "")
      : description;
    const splitString = updatedString.split(/,(.*)/s);
    if (isMainheader) {
      return splitString[0];
    }
    return splitString[1];
  };

  const getListUnitPrice = (lineItem) => {
    const parsedListPrice = Number.parseFloat(lineItem?.listPrice);
    if (Number.isFinite(parsedListPrice) && parsedListPrice > 0) {
      return parsedListPrice;
    }
    const parsedNetPrice = Number.parseFloat(lineItem?.unitPrice);
    return Number.isFinite(parsedNetPrice) ? parsedNetPrice : 0;
  };

  const getNetUnitPrice = (lineItem) => {
    const parsedNetPrice = Number.parseFloat(lineItem?.unitPrice);
    if (Number.isFinite(parsedNetPrice)) {
      return parsedNetPrice;
    }
    return getListUnitPrice(lineItem);
  };

  const getTotalUnitPrice = (lineItem) => {
    const parsedQty = Number.parseFloat(lineItem?.quantity);
    const qty = Number.isFinite(parsedQty) ? parsedQty : 0;
    return getNetUnitPrice(lineItem) * qty;
  };

  const pdfStyle = `
    @page {
      margin: 1.3cm;
    }`;

  return (
    <div className="print-quote-wrapper" ref={ref} style={{ width: '100%', padding: "50px auto", fontFamily: 'var(--sv-font-sans)' }}>
      <style>{pdfStyle}</style>
      <div
          className="quote-container"
          style={{ width: "100%", fontFamily: 'var(--sv-font-sans)' }}
        >
          <div className="quote-header">
            <table width="100%" border={0} style={{ border: 0 }}>
              <tbody>
                <tr style={{backgroundColor: "transparent"}}>
                  <td valign="bottom" style={{ paddingBottom: 30 }}>
                    <div className="3m-logo" align="left">
                      <img
                        src={threeMlogo}
                        alt="Solventum logo"
                        width={180}
                        style={{ verticalAlign: "bottom" }}
                      />
                    </div>
                  </td>
                  <td align="right" style={{ paddingBottom: 30 }} valign="bottom">
                    <h3
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        fontSize: 26,
                        margin: 0,
                        lineHeight: "100%"
                      }}
                    >
                      Shop Ortho Order Quote
                    </h3>
                  </td>
                </tr>
                <tr style={{backgroundColor: "transparent"}}>
                  <td width="55%" align="left">
                    <div className="textGroup" style={{ marginBottom: 8 }}>
                      <span
                        className="quote_label"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#000000",
                          minWidth: 145,
                          textAlign: "left",
                          display: "table-cell"
                        }}
                      >
                        Account Number:
                      </span>
                      <span
                        className="quote_field"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          color: "#000000",
                          display: "table-cell",
                          minWidth: 110,
                          textAlign: "left"
                        }}
                      >
                        {userDetails?.customerNumber}
                      </span>
                    </div>
                    <div className="textGroup" style={{ marginBottom: 8 }}>
                      <span
                        className="quote_label"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#000000",
                          minWidth: 145,
                          textAlign: "left",
                          display: "table-cell"
                        }}
                      >
                        Address:
                      </span>
                      <span
                        className="quote_field"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 15,
                          color: "#000000",
                          display: "table-cell",
                          minWidth: 110,
                          textAlign: "left"
                        }}
                      >
                        <span style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 15,
                          color: "#000000",
                          display: "block",
                        }}>{userDetails?.billingFirstName} {userDetails?.billingLastName}</span>
                        <span style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 15,
                          color: "#000000",
                          display: "block",
                        }}>{userDetails?.billingAddressLine1}</span>
                        <span style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 15,
                          color: "#000000",
                          display: "block",
                        }}>{userDetails?.billingCity}, {userDetails?.billingState}, {userDetails?.billingZipCode}, {userDetails?.billingCountry}</span>
                      
                      </span>
                    </div>
                  </td>
                  <td width="45%" align="right">
                    <div className="textGroup" style={{ marginBottom: 8 }}>
                      <span
                        className="quote_label"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#000000",
                          minWidth: 145,
                          textAlign: "left",
                          display: "table-cell"
                        }}
                      >
                        Quote Number:
                      </span>
                      <span
                        className="quote_field"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          color: "#000000",
                          display: "table-cell",
                          minWidth: 110,
                          textAlign: "left"
                        }}
                      >
                        {orderId}
                      </span>
                    </div>
                    <div className="textGroup" style={{ marginBottom: 8 }}>
                      <span
                        className="quote_label"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#000000",
                          minWidth: 145,
                          textAlign: "left",
                          display: "table-cell"
                        }}
                      >
                        Quote Date:
                      </span>
                      <span
                        className="quote_field"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          color: "#000000",
                          display: "table-cell",
                          minWidth: 110,
                          textAlign: "left"
                        }}
                      >
                        {quoteDate}
                      </span>
                    </div>
                    <div className="textGroup" style={{ marginBottom: 8 }}>
                      <span
                        className="quote_label"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          fontWeight: "bold",
                          color: "#000000",
                          minWidth: 145,
                          textAlign: "left",
                          display: "table-cell"
                        }}
                      >
                        Quote Amount:
                      </span>
                      <span
                        className="quote_field"
                        style={{
                          fontFamily: 'var(--sv-font-sans)',
                          fontSize: 16,
                          color: "#000000",
                          display: "table-cell",
                          minWidth: 110,
                          textAlign: "left"
                        }}
                      >
                        {currencyFormatter.format(totalCost)}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <span
            style={{
              width: "100%",
              border: "2px solid #29c7fb",
              margin: "15px 0 30px",
              display: "block"
            }}
          />
          <div className="quote-body">
            <table
              width="100%"
              className="quote-table"
              border={1}
              style={{
                width: "100%",
                borderCollapse: "collapse",
                borderColor: "#f2f2f5"
              }}
            >
              <tbody>
                <tr style={{backgroundColor: "transparent"}}>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    Part Number
                  </th>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    Items
                  </th>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    Qty
                  </th>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    Unit
                  </th>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    List Unit Price
                  </th>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    Net Unit Price
                  </th>
                  <th
                    style={{
                      fontFamily: 'var(--sv-font-sans)',
                      padding: 5,
                      fontSize: 14,
                      color: "#000000",
                      backgroundColor: "#ffffff"
                    }}
                    align="center"
                  >
                    Total Unit Price
                  </th>
                </tr>
                {shoppingCartItems.map((productItem, productIndex) => (
                  <tr key={productIndex.toString()} style={{backgroundColor: "transparent"}}>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="left"
                    >
                      {productItem?.partNumber}
                    </td>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="left"
                    >
                      {productItem.bandOrderData
                                  ? productItem.bandOrderData.header
                                  : splitProductDescription(
                                      productItem.shortDescription,
                                      productItem?.partNumber,
                                      true
                      )}, 
                      {productItem?.partNumber},
                      {productItem.bandOrderData
                      ? productItem.bandOrderData.header
                      : splitProductDescription(
                          productItem.shortDescription,
                          productItem?.partNumber,
                          false
                      )}
                    </td>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="center"
                    >
                      {productItem.quantity.toString().replace(/\.0$/,'')}
                    </td>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="center"
                    >
                      {productItem.xitem_field2}
                    </td>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="right"
                    >
                      {currencyFormatter.format(getListUnitPrice(productItem))}
                    </td>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="right"
                    >
                      {currencyFormatter.format(getNetUnitPrice(productItem))}
                    </td>
                    <td
                      style={{
                        fontFamily: 'var(--sv-font-sans)',
                        padding: 5,
                        fontSize: 14,
                        color: "#000000",
                        backgroundColor: "#ffffff"
                      }}
                      align="right"
                    >
                      {currencyFormatter.format(getTotalUnitPrice(productItem))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  )
}
);
export default PrintQuote;
